import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Auth } from '@shared/auth';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { DATASTORE_CONFIG } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';

import {
  backend,
  BackendConfig,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  Params,
} from './backend';
import { isDefined, ReferenceList } from './helpers';
import { RootModel } from './root.model';

export type ApiResponseStatus = 'success' | 'error';

export type ApiFetchResponse<T extends keyof RootModel> =
  | { status: 'success'; result: BackendFetchTypes[T] }
  | { status: 'error'; message: string };

export type ApiPostResponse<T extends keyof RootModel> =
  | { status: 'success'; result: BackendPushReturnTypes[T] }
  | { status: 'error'; message: string };

@Injectable()
export class ApiHttp {
  private _requestStream: Subject<ReferenceList<any>>;
  get requestStream(): Observable<ReferenceList<any>> {
    return this._requestStream.asObservable();
  }
  constructor(
    private http: HttpClient,
    private auth: Auth,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
  ) {
    this._requestStream = new Subject();
  }

  fetch<T extends keyof RootModel>(
    ref: ReferenceList<T>,
  ): Observable<ApiFetchResponse<T>> {
    return Observable.of(ref)
      .switchMap(({ pathList, query }) =>
        Observable.of(backend[pathList[0]].fetch)
          .filter(isDefined)
          .map(fetch => ({
            newConfig: fetch(pathList[1], query),
            pagination: {
              limit: query && query.limit,
              offset: query && query.offset,
            },
          })),
      )
      .withLatestFrom(this.auth.getAuthorizationHeader())
      .switchMap(([{ newConfig, pagination }, authHeader]) => {
        const baseUrl = newConfig.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;
        const params = pagination
          ? {
              ...newConfig.params,
              limit: pagination.limit,
              offset: pagination.offset,
            }
          : newConfig.params;
        return this.http.get<
          ApiFetchResponse<T>
        >(`${baseUrl}/${newConfig.endpoint}`, {
          headers: authHeader,
          params: this.serialize(params),
        });
      })
      .do(() => this._requestStream.next(ref))
      .catch((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          return Observable.throw(err.error);
        }
        // The backend returned an unsuccessful response code.
        return Observable.throw(new Error(err.message));
      });
  }

  post<T extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[T],
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'post', payload });
  }

  put<T extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[T],
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'put', payload });
  }

  delete<T extends keyof RootModel>(
    config: BackendConfig,
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'delete' });
  }

  private mutate<T extends keyof RootModel>(
    config: BackendConfig,
    data:
      | { action: 'post'; payload: BackendPushPayloadTypes[T] }
      | { action: 'put'; payload: BackendPushPayloadTypes[T] }
      | { action: 'delete' },
  ): Observable<ApiPostResponse<T>> {
    return Observable.of(config)
      .withLatestFrom(this.auth.getAuthorizationHeader())
      .switchMap(([newConfig, authHeader]) => {
        const baseUrl = newConfig.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const url = `${baseUrl}/${newConfig.endpoint}`;
        const options = { headers: authHeader };

        if (data.action === 'delete') {
          return this.http.delete<ApiPostResponse<T>>(url, options);
        }

        const body = this.serialize({
          ...(<Params>data.payload), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
          ...newConfig.params,
        });

        if (data.action === 'post') {
          return this.http.post<ApiPostResponse<T>>(url, body, options);
        }
        return this.http.put<ApiPostResponse<T>>(url, body, options);
      });
  }

  private serialize(obj: Partial<Params>): HttpParams {
    let params = new HttpParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'string') {
        params = params.set(key, value);
      } else if (typeof value === 'number') {
        params = params.set(key, String(value));
      } else if (Array.isArray(value)) {
        // This type casting shouldn't be necessary but is because of a TS bug.
        // https://github.com/Microsoft/TypeScript/issues/15315
        // https://github.com/Microsoft/TypeScript/issues/7294

        // Our backend expects arrays foo=[1,2] to be encoded as foo[]=1&foo[]=2
        (value as (string | number)[]).forEach(rawVal =>
          // The batching of requests connatenates things with `&`.
          String(rawVal)
            .split('&')
            .forEach(v => (params = params.append(`${key}[]`, v))),
        );
      }
    });
    params = params.set('compact', 'true');
    return params;
  }

  /*
  private handleException(error: Response | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
  */
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/ko/webapp/src/app/@shared/datastore/api-http.service.ts